import { Stack, Tabs } from '@mantine/core';
import { trackEvent } from '@repo/common/analytics';
import { FilterOptionType } from '@repo/common/components/Filter';
import type { EmptyStateButtons } from '@repo/common/components/NewEmptyState/EmptyState';
import { EmptyState } from '@repo/common/components/NewEmptyState/EmptyState';
import TabsList from '@repo/common/components/TabsList';
import { integrationList } from '@repo/common/constants/integration/integrations';
import { UserRole } from '@repo/common/enums/UserRole';
import { Title } from '@repo/foundations';
import { width } from '@repo/theme/primitives';
import { isEmpty } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useAuthUser, useIntegrationList } from '../../api';
import { FeatureGuard } from '../../components/FeatureGuard/FeatureGuard';
import { FILTER_OPTIONS_CONFIG } from '../../components/Filter/constants';
import AddIntegrationModal from '../../components/Integration/AddIntegrationModal';
import { useStyles as useAddIntegrationModalStyles } from '../../components/Integration/AddIntegrationModal/AddIntegrationModal.styles';
import { NEW_INTEGRATION_MODAL_ID } from '../../components/Integration/AddIntegrationModal/constants';
import { FullWidthLoadingSpinner } from '../../components/LoadingSpinner';
import { openModal } from '../../components/ModalManager';
import { ExtensionSettings } from '../../components/Settings/ExtensionSettings/ExtensionSettings';
import { TableV2 } from '../../components/TableV2';
import type { ExtendedDataTableColumn } from '../../components/TableV2/types';
import { IntegrationsOverviewTabController } from '../../features/Overview/IntegrationsOverviewTabController';
import type {
	IntegrationMetricCardType,
	MetricCardType,
} from '../../features/Overview/types';
import { useFeatureFlags } from '../../utils/featureFlags';
import { checkFeatureFlag } from '../../utils/featureFlags/featureFlagUtils';
import { useListPageTabNavigate } from '../../utils/hook/useTabNavigate';
import { useStyles } from '../TeamCatalogPage/TeamCatalogPage.styles';
import { useActions, useColumns } from './IntegrationsPage.hooks';

type IntegrationsPageTab = 'overview' | 'integrations' | 'extensions';

const METRIC_FILTERS: Record<
	IntegrationMetricCardType,
	{ value: string; operator: string; filterType: string }[]
> = {
	'Completed Sync': [
		{ value: 'success', operator: 'is', filterType: 'sync_status' },
	],
	Failed: [{ value: 'failed', operator: 'is', filterType: 'sync_status' }],
};

const FILTER_OPTIONS = [FILTER_OPTIONS_CONFIG[FilterOptionType.SYNC_STATUS]];

const COLUMN_VISIBILITY = {
	catalogServerType: 'integrations',
	catalogType: 'integration',
} as const;

const QUICK_ACTIONS = ['actions::run', 'actions::delete'] as const;

const PAGINATION_LIST_OPTIONS = {
	suspense: true,
};

const SETTINGS_PAGE_ONLY_TYPES = integrationList
	.filter((i) => i.isExtension)
	.map((i) => i.type)
	.join(',');

const DEFAULT_REQUIRED_SEARCH_PARAMS = {
	type__not_in: SETTINGS_PAGE_ONLY_TYPES,
};

function IntegrationsPage() {
	const { data: allIntegrations, isLoading: isLoadingAllIntegrations } =
		useIntegrationList({
			page: 1,
			filters: DEFAULT_REQUIRED_SEARCH_PARAMS,
		});

	const { user, workspace } = useAuthUser();
	const { classes } = useStyles();
	const { classes: addIntegrationModalClasses } =
		useAddIntegrationModalStyles();
	const { nativeSnowflake, extensionsInIntegrationsPage } = useFeatureFlags();
	const navigateToTab = useListPageTabNavigate();
	const navigate = useNavigate();
	const [activeTab, setActiveTab] = useState<IntegrationsPageTab>(() => {
		const { pathname } = window.location;
		if (pathname.endsWith('/overview')) return 'overview';
		if (pathname.endsWith('/extensions')) return 'extensions';
		return 'integrations';
	});

	const columns = useColumns();
	const actions = useActions();

	const tabs = useMemo(() => {
		const base = [
			{ value: 'overview', label: 'Overview' },
			{ value: 'integrations', label: 'Integrations' },
		];
		if (extensionsInIntegrationsPage) {
			base.push({ value: 'extensions', label: 'Extensions' });
		}
		return base;
	}, [extensionsInIntegrationsPage]);

	const handleClick = useCallback(
		(id: string) => {
			navigate(`/integrations/${id}`);
		},
		[navigate]
	);

	const handleTabChange = useCallback(
		(value: string): void => {
			if (
				value === 'integrations' ||
				value === 'overview' ||
				value === 'extensions'
			) {
				setActiveTab(value as IntegrationsPageTab);
				navigateToTab(value);
			}
			trackEvent(
				'integrations/tabs/click',
				{
					tab: value,
				},
				user,
				workspace!
			);
		},
		[navigateToTab, setActiveTab, user, workspace]
	);

	const featureFlags = useFeatureFlags();
	const availableIntegrations = useMemo(() => {
		const source = extensionsInIntegrationsPage
			? integrationList
			: integrationList.filter((i) => !i.isExtension);
		return source
			.filter((i) => {
				if (i.type === 'native_snowflake') {
					return nativeSnowflake;
				}
				return true;
			})
			.filter(
				(i) => !i.featureFlag || checkFeatureFlag(i.featureFlag, featureFlags)
			)
			.map((i) => i.type);
	}, [nativeSnowflake, featureFlags, extensionsInIntegrationsPage]);

	const handleConnectIntegration = useCallback(() => {
		const isExtensionsTab = activeTab === 'extensions';
		const extensionTypes = integrationList
			.filter((i) => i.isExtension)
			.map((i) => i.type);

		openModal({
			modalId: NEW_INTEGRATION_MODAL_ID,
			title: (
				<Title size="md">
					{isExtensionsTab ? 'Connect Extension' : 'Connect integration'}
				</Title>
			),
			children: (
				<AddIntegrationModal
					modalType={isExtensionsTab ? 'extension' : 'integration'}
					integrationTypes={
						isExtensionsTab ? extensionTypes : availableIntegrations
					}
				/>
			),
			size: width.xxl,
			radius: 'lg',
			classNames: {
				header: addIntegrationModalClasses.header,
				body: addIntegrationModalClasses.body,
			},
		});
	}, [
		addIntegrationModalClasses.body,
		addIntegrationModalClasses.header,
		availableIntegrations,
		activeTab,
	]);

	const onCellClick = useCallback(
		(cell) => {
			if (
				(cell.column as ExtendedDataTableColumn<unknown>).navigate !== false
			) {
				handleClick(cell.record.id);
			}
		},
		[handleClick]
	);

	const emptyStateButtons = useMemo(
		() => [
			{
				label: 'Learn more',
				variant: 'default',
				onClick: () => {
					window.open('https://docs.secoda.com/integrations', '_blank');
				},
			},
			{
				label:
					activeTab === 'extensions'
						? 'Connect extension'
						: 'Connect integration',
				variant: 'primary',
				onClick: handleConnectIntegration,
			},
		],
		[handleConnectIntegration, activeTab]
	);

	const handleMetricCardClick = useCallback(
		(metricType: IntegrationMetricCardType | MetricCardType) => {
			const filters = METRIC_FILTERS[metricType as IntegrationMetricCardType];

			navigate(
				`/integrations?filters=${encodeURIComponent(JSON.stringify(filters))}`
			);
			setActiveTab('integrations');
			trackEvent(
				'integrations/overview/metric_card/click',
				{
					metric_type: metricType,
				},
				user,
				workspace!
			);
		},
		[navigate, setActiveTab, user, workspace]
	);

	const integrationsList = useMemo(
		() => allIntegrations?.results ?? [],
		[allIntegrations?.results]
	);

	if (isLoadingAllIntegrations) {
		return <FullWidthLoadingSpinner />;
	}

	if (!isLoadingAllIntegrations && isEmpty(integrationsList)) {
		const isExtensionsTab = activeTab === 'extensions';
		return (
			<EmptyState
				heading={
					isExtensionsTab
						? 'Connect your first extension'
						: 'Connect your first integration'
				}
				body={
					isExtensionsTab
						? 'Set up two or more extensions to seamlessly interact, govern and view lineage with your data.'
						: 'Set up two or more integrations to seamlessly interact, govern and view lineage with your data.'
				}
				illustrationName="integrations"
				buttons={emptyStateButtons as EmptyStateButtons}
			/>
		);
	}

	return (
		<FeatureGuard
			featureName="Integrations"
			description="Get access to integrations to seamlessly interact, govern and view lineage with your data."
			illustrationName="integrations"
			v1AllowedRoles={[UserRole.ADMIN, UserRole.EDITOR]}
			v2Permission="Integrations.Read"
			isFeatureIncludedInPlan={true}
			isAlwaysIncludedFeature={false}
		>
			<Stack className={classes.featureWrapper}>
				<Tabs
					color="fill/brand/default"
					value={activeTab}
					onTabChange={handleTabChange}
					keepMounted={false}
				>
					<TabsList tabs={tabs} />
					<Tabs.Panel value="overview" pt="sm">
						<IntegrationsOverviewTabController
							integrations={integrationsList}
							onMetricCardClick={handleMetricCardClick}
						/>
					</Tabs.Panel>
					<Tabs.Panel value="integrations" pt="sm">
						<TableV2
							columnVisibility={COLUMN_VISIBILITY}
							pluralTypeString="integrations"
							columns={columns}
							usePaginationList={useIntegrationList}
							defaultRequiredSearchParams={DEFAULT_REQUIRED_SEARCH_PARAMS}
							withFilterOptions={FILTER_OPTIONS}
							usePaginationListOptions={PAGINATION_LIST_OPTIONS}
							withActions={actions}
							withCheckbox={true}
							withQuickActions={QUICK_ACTIONS}
							withCsvExport
							withInteractiveHeader
							withSearch
							defaultSort={null}
							onCellClick={onCellClick}
							paginationSize={1000}
						/>
					</Tabs.Panel>
					{extensionsInIntegrationsPage && (
						<Tabs.Panel value="extensions" pt="sm">
							<ExtensionSettings />
						</Tabs.Panel>
					)}
				</Tabs>
			</Stack>
		</FeatureGuard>
	);
}

export default IntegrationsPage;
